import { useEffect, useRef, useState } from 'react';
import { Ban, Check, Loader2, Phone, Play, Save, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useSaveCallContact } from '../../hooks/use-call-mutations';
import { useCall } from '../../hooks/use-calls';
import { useFetchRecordingUrl } from '../../hooks/use-recordings';
import {
    CALL_DISPOSITIONS,
    type CallDisposition,
    type IUpdateCallDispositionInput,
} from '../../types';
import { CallerIdentity } from './caller-identity';
import { SaveContactForm } from './save-contact-form';

/** Sentinel for the "no outcome picked" Select value (Radix forbids ''). */
const NO_DISPOSITION = 'NONE';

const DISPOSITION_LABELS: Record<CallDisposition, string> = {
    INTERESTED: 'Interested',
    NOT_INTERESTED: 'Not interested',
    FOLLOW_UP: 'Follow up',
    OTHERS: 'Other',
};

interface PostCallCardDraft {
    readonly disposition: CallDisposition | null;
    readonly description: string;
    readonly notes: string;
}

interface PostCallCardProps {
    readonly callId: string;
    /**
     * Persist notes/disposition. Owned by the widget (not this card) so the
     * PATCH still fires when the card unmounts on answer/dial dismissal —
     * the card passes its own `callId` since the store's may already be cleared.
     */
    readonly onCommit: (callId: string, payload: IUpdateCallDispositionInput) => void;
    /** True while the disposition PATCH is in flight — drives the Save spinner. */
    readonly isSaving: boolean;
    /** Dismiss the card (clears the store's post-call card id). */
    readonly onClose: () => void;
    /** Redial the counterpart — the widget guards it while a call is active. */
    readonly onCallAgain: (toNumber: string) => void;
    readonly canCallAgain: boolean;
    /** Block the counterpart org-wide (also flips SMS opt-out server-side). */
    readonly onBlacklist: (phone: string) => void;
    readonly isBlocking: boolean;
}

/** Turns a draft into a PATCH payload, dropping empty fields. */
function draftToPayload(draft: PostCallCardDraft): IUpdateCallDispositionInput {
    const description = draft.description.trim();
    const notes = draft.notes.trim();
    return {
        ...(draft.disposition ? { disposition: draft.disposition } : {}),
        ...(description ? { description } : {}),
        ...(notes ? { notes } : {}),
    };
}

function isEmptyPayload(payload: IUpdateCallDispositionInput): boolean {
    return (
        payload.disposition === undefined &&
        payload.description === undefined &&
        payload.notes === undefined
    );
}

/**
 * Inline post-call card — replaces the modal disposition dialog. Shows the
 * line, contact, recording (processing → player), a note + optional outcome,
 * and Call again / Close / Black list. The ringing queue renders above it.
 *
 * Non-blocking: answering a queued call or dialing dismisses the card, and
 * that dismissal commits a non-empty note draft (via the unmount cleanup) so
 * a typed note is never silently dropped — the same commit path "Close" uses.
 */
export function PostCallCard({
    callId,
    onCommit,
    isSaving,
    onClose,
    onCallAgain,
    canCallAgain,
    onBlacklist,
    isBlocking,
}: PostCallCardProps) {
    const { data: call, isLoading } = useCall(callId);
    const fetchUrl = useFetchRecordingUrl();
    const saveContact = useSaveCallContact();

    const [disposition, setDisposition] = useState<CallDisposition | null>(null);
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [othersError, setOthersError] = useState(false);
    const [confirmingBlock, setConfirmingBlock] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [addingContact, setAddingContact] = useState(false);

    // One-shot guard so Close and the unmount dismissal never double-PATCH.
    // Explicit Save clears it again on the next edit (see `markDirty`) — without
    // that, a save-edit-save sequence would silently drop the second save.
    const committedRef = useRef(false);
    /** Drives the inline "Saved" marker; cleared the moment a field changes. */
    const [isSaved, setIsSaved] = useState(false);

    /** Any edit re-arms the commit guard and retires the Saved marker. */
    const markDirty = (): void => {
        committedRef.current = false;
        setIsSaved(false);
    };

    // The single note-commit path — shared by explicit Close (strict: OTHERS
    // needs a description) and implicit answer/dial dismissal (lenient: drop an
    // incomplete OTHERS but keep the note). Reads live state via closure.
    const commit = (allowDropInvalidOthers: boolean): 'ok' | 'invalid' => {
        if (committedRef.current) return 'ok';
        let effectiveDisposition = disposition;
        if (effectiveDisposition === 'OTHERS' && !description.trim()) {
            if (!allowDropInvalidOthers) return 'invalid';
            effectiveDisposition = null;
        }
        const payload = draftToPayload({ disposition: effectiveDisposition, description, notes });
        // Set the one-shot guard ONLY when a PATCH actually fires. StrictMode
        // runs the unmount effect's cleanup once at mount, invoking this with an
        // empty draft — guarding unconditionally there would poison it and drop
        // the real Close/dismissal commit.
        if (!isEmptyPayload(payload)) {
            committedRef.current = true;
            onCommit(callId, payload);
        }
        return 'ok';
    };

    // Answering a queued call or dialing unmounts the card; commit leniently on
    // unmount so a typed note survives. A "latest committer" ref (updated in an
    // effect, invoked only in the unmount cleanup) keeps the closure fresh
    // without reading/writing a ref during render.
    const committerRef = useRef<() => void>(() => {});
    useEffect(() => {
        committerRef.current = () => void commit(true);
    });
    useEffect(() => () => committerRef.current(), []);

    if (!call && isLoading) {
        return (
            <div
                data-slot="voice-post-call-card"
                className="flex items-center justify-center gap-2 p-6 text-sm text-text-tertiary"
            >
                <Loader2 className="size-4 animate-spin" />
                Loading call…
            </div>
        );
    }

    const contactName = call?.contact
        ? [call.contact.firstName, call.contact.lastName].filter(Boolean).join(' ')
        : null;
    const counterpart = call
        ? call.direction === 'OUTBOUND'
            ? call.toNumber
            : call.fromNumber
        : '';
    const lineLabel = call
        ? (call.channelIdentity.displayName ?? call.channelIdentity.identity)
        : '';

    const storedRecording = call?.recordings.find((r) => r.status === 'STORED') ?? null;
    const isRecordingProcessing = Boolean(call?.recordingEnabled) && !storedRecording;

    // Calls never auto-create contacts — offer an explicit save only when the
    // counterpart isn't already a saved contact.
    const canSaveContact = Boolean(call && !call.contact && counterpart);

    const handleClose = (): void => {
        if (commit(false) === 'invalid') {
            setOthersError(true);
            return;
        }
        onClose();
    };

    /**
     * Explicit save that keeps the card open. The ticket's complaint was that
     * notes were only ever persisted on dismissal, so the user had no way to
     * confirm their work landed without closing the widget.
     */
    const handleSave = (): void => {
        if (commit(false) === 'invalid') {
            setOthersError(true);
            return;
        }
        setIsSaved(true);
    };

    const hasDraft = !isEmptyPayload(draftToPayload({ disposition, description, notes }));

    const handleCallAgain = (): void => {
        if (!counterpart) return;
        onCallAgain(counterpart);
    };

    const handleConfirmBlock = (): void => {
        if (!counterpart) return;
        onBlacklist(counterpart);
        onClose();
    };

    const handlePlayRecording = (): void => {
        if (!storedRecording) return;
        fetchUrl.mutate(storedRecording.id, {
            onSuccess: (result) => setAudioUrl(result.url),
        });
    };

    const handleSaveContact = (name: string): void => {
        if (!counterpart) return;
        saveContact.mutate(
            { callId, firstName: name, phone: counterpart },
            {
                // On success the call refetches with its linked contact, so the
                // whole section unmounts — just reset the local form.
                onSuccess: () => setAddingContact(false),
            },
        );
    };

    return (
        <div
            data-slot="voice-post-call-card"
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-[calc(1rem*var(--voice-scale,1))]"
        >
            <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-[0.12em] text-text-secondary uppercase">
                    Call wrap-up
                </span>
                {lineLabel && (
                    <span className="truncate text-xs text-text-tertiary" title={lineLabel}>
                        on {lineLabel}
                    </span>
                )}
            </div>

            <CallerIdentity name={contactName} number={counterpart} />

            {(isRecordingProcessing || storedRecording) && (
                <div className="rounded-xl border border-white/50 bg-white/40 p-2 dark:border-white/10 dark:bg-white/[0.04]">
                    {isRecordingProcessing ? (
                        <p className="flex items-center gap-2 text-xs text-text-tertiary">
                            <Loader2 className="size-3.5 animate-spin" />
                            Processing recording…
                        </p>
                    ) : audioUrl ? (
                        <audio src={audioUrl} controls autoPlay className="h-8 w-full" />
                    ) : (
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="w-full gap-2"
                            disabled={fetchUrl.isPending}
                            onClick={handlePlayRecording}
                        >
                            {fetchUrl.isPending ? (
                                <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                                <Play className="size-3.5" />
                            )}
                            Play recording
                        </Button>
                    )}
                </div>
            )}

            {canSaveContact &&
                (addingContact ? (
                    <SaveContactForm
                        idPrefix="post-call"
                        phone={counterpart}
                        isSaving={saveContact.isPending}
                        onSave={handleSaveContact}
                        onCancel={() => setAddingContact(false)}
                    />
                ) : (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setAddingContact(true)}
                    >
                        <UserPlus className="size-3.5" />
                        Save to contacts
                    </Button>
                ))}

            <div className="flex flex-col gap-1.5">
                <Label htmlFor="post-call-notes">Notes</Label>
                <Textarea
                    id="post-call-notes"
                    rows={2}
                    placeholder="Anything worth remembering"
                    value={notes}
                    onChange={(event) => {
                        setNotes(event.target.value);
                        markDirty();
                    }}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <Label htmlFor="post-call-outcome">Outcome</Label>
                <Select
                    value={disposition ?? NO_DISPOSITION}
                    onValueChange={(value) => {
                        setDisposition(
                            value === NO_DISPOSITION ? null : (value as CallDisposition),
                        );
                        setOthersError(false);
                        markDirty();
                    }}
                >
                    <SelectTrigger id="post-call-outcome" size="sm" className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-1001">
                        <SelectItem value={NO_DISPOSITION}>No outcome</SelectItem>
                        {CALL_DISPOSITIONS.map((value) => (
                            <SelectItem key={value} value={value}>
                                {DISPOSITION_LABELS[value]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {disposition === 'OTHERS' && (
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="post-call-description" required>
                        Description
                    </Label>
                    <Textarea
                        id="post-call-description"
                        rows={2}
                        placeholder="What happened on this call?"
                        value={description}
                        onChange={(event) => {
                            setDescription(event.target.value);
                            if (event.target.value.trim()) setOthersError(false);
                            markDirty();
                        }}
                    />
                    {othersError && (
                        <p className="text-xs text-destructive">
                            A description is required when the outcome is “Other”.
                        </p>
                    )}
                </div>
            )}

            {confirmingBlock ? (
                <div className="flex flex-col gap-2 rounded-xl border border-error-200 bg-error-50/90 p-2.5 dark:border-error-900 dark:bg-error-950/40">
                    <p className="text-xs text-text-secondary">
                        Block {contactName ?? counterpart}? Once blocked, they are no longer able to
                        call or send messages.
                    </p>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            disabled={isBlocking}
                            onClick={() => setConfirmingBlock(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="flex-1 gap-1.5"
                            disabled={isBlocking || !counterpart}
                            onClick={handleConfirmBlock}
                        >
                            {isBlocking ? (
                                <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                                <Ban className="size-3.5" />
                            )}
                            Block
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {/* Save keeps the card open and confirms in place — the
                        whole point of the ticket was knowing the note landed
                        without having to dismiss the widget to find out. */}
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            className="flex-1 gap-1.5"
                            disabled={!hasDraft || isSaving || isSaved}
                            onClick={handleSave}
                        >
                            {isSaving ? (
                                <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                                <Save className="size-3.5" />
                            )}
                            {/* "Save wrap-up", not bare "Save" — the inline
                                save-to-contacts form has its own Save button
                                and can be open at the same time. */}
                            {isSaving ? 'Saving…' : 'Save wrap-up'}
                        </Button>
                        {isSaved && !isSaving && (
                            <span
                                role="status"
                                className="flex shrink-0 items-center gap-1 text-xs font-medium text-success-600 dark:text-success-400"
                            >
                                <Check className="size-3.5" />
                                Saved
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            className="gap-2"
                            disabled={!canCallAgain || !counterpart}
                            title={canCallAgain ? undefined : 'Finish the current call first'}
                            onClick={handleCallAgain}
                        >
                            <Phone className="size-4" />
                            Call again
                        </Button>
                        <Button type="button" onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-text-tertiary hover:text-destructive"
                        disabled={!counterpart}
                        onClick={() => setConfirmingBlock(true)}
                    >
                        <Ban className="size-3.5" />
                        Black list
                    </Button>
                </div>
            )}
        </div>
    );
}
